import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';

export function DocsLandingPage() {
  return (
    <DocsLayout
      title="Documentation"
      updated="August 26, 2026"
      summary="Governed Quality Intelligence for change-aware Quality Engineering — from code change to Release Confidence and evidence export. Rynora is the system of record for product quality."
    >
      <h2>Start here</h2>
      <div className="docs-card-grid">
        <Link to="/docs/getting-started" className="docs-card">
          <h3>Quickstart</h3>
          <p>Create a project, publish a flow, run it, and open Quality HQ.</p>
        </Link>
        <Link to="/docs/api" className="docs-card">
          <h3>API reference</h3>
          <p>Authenticate, trigger runs, and integrate CI — without inventing coverage claims.</p>
        </Link>
        <Link to="/docs/browser-tiers" className="docs-card">
          <h3>Browser tiers</h3>
          <p>How execution targets differ — SaaS headless, private agent, and BYO grids.</p>
        </Link>
        <Link to="/docs/self-hosting" className="docs-card">
          <h3>Self-hosting</h3>
          <p>Run Rynora on your infrastructure when sovereignty requires it.</p>
        </Link>
      </div>

      <h2>Quality Intelligence spine</h2>
      <p>
        In the product Help Center (open Help from the app), see guides for Change Impact, Test Impact Analysis,
        targeted regression, Failure Intelligence, Release Confidence, evidence export, and Playwright project egress.
        Public pages stay brief; the app Help carries operator depth.
      </p>
      <ul>
        <li>
          <strong>Change-aware testing</strong> — bind GitLab/Bitbucket (or GitHub), compare a SHA, review Change Impact
          and TIA recommendations.
        </li>
        <li>
          <strong>Failure Intelligence</strong> — classify failures and attach resolution evidence where issue links
          exist.
        </li>
        <li>
          <strong>Release Confidence</strong> — advisory ship/hold with missing evidence named.
        </li>
        <li>
          <strong>Evidence &amp; export</strong> — portable quality evidence bundle; published Playwright/TS project
          export without runtime-parity claims.
        </li>
      </ul>

      <h2>Account &amp; billing</h2>
      <div className="docs-card-grid">
        <Link to="/docs/plans" className="docs-card">
          <h3>Plans &amp; limits</h3>
          <p>What every plan includes and how features unlock as you move up.</p>
        </Link>
        <Link to="/docs/billing" className="docs-card">
          <h3>Billing &amp; invoices</h3>
          <p>How Rynora bills and who can manage the subscription.</p>
        </Link>
        <Link to="/docs/user-roles" className="docs-card">
          <h3>User roles</h3>
          <p>Admins, Users, and Owner — who can do what inside an organisation.</p>
        </Link>
        <Link to="/docs/delete-and-restore" className="docs-card">
          <h3>Delete &amp; restore</h3>
          <p>What happens when you delete a project and how restore works.</p>
        </Link>
      </div>

      <h2>Authoring</h2>
      <div className="docs-card-grid">
        <Link to="/docs/script-step" className="docs-card">
          <h3>Custom Script step</h3>
          <p>TypeScript, Python, or Java in a flow when built-in steps are not enough.</p>
        </Link>
      </div>

      <h2>What is Rynora?</h2>
      <p>
        Rynora is the system of record for product quality — governed Quality Intelligence for change-aware Quality
        Engineering. It connects what changed, what should be tested, what failed, what needs maintenance attention, and
        what evidence supports an advisory release decision. AI accelerates authoring and analysis; deterministic
        services drive execution, change intelligence, diagnosis, governance, and evidence. Automation runs are sensors
        in that spine — not the product category by themselves.
      </p>

      <h2>Bug or doc gap?</h2>
      <p>
        Email <a href="mailto:support@rynora.app">support@rynora.app</a> or open an issue at{' '}
        <a href="https://github.com/rynoratech/rynora/issues" target="_blank" rel="noreferrer">
          github.com/rynoratech/rynora
        </a>
        .
      </p>
    </DocsLayout>
  );
}
